import React, {FunctionComponent, useEffect, useRef, useState} from "react";
import {IAppComponentProps} from "../../../components";
import enUS from "antd/lib/locale/en_US";
import deDe from "antd/lib/locale/de_DE";
import {ConfigProvider, Spin, Tabs} from "antd";
import {InfoPortalTabConfig, Initialization} from "../InfoPortalInterfaces";
import {v4 as uuidv4} from 'uuid';
import {entitlementExists} from "../../../main/entitlementService";
import {ProPageHeader} from "@ant-design/pro-layout";
import {TNRReportPortalTab, TNRTab} from "./tabs/TNRReportTab";

type AllTabsState = {
    isExpertMode: boolean;
    reportName: string;
}
const AccountReceivableReports: FunctionComponent<IAppComponentProps> = (props) => {

    const [selectedTab, setSelectedTab] = useState<string>("item-1")
    const [allTabStates, setAllTabStates] = useState<{ [key: string]: AllTabsState }>({});

    const tnrTab = useRef<InfoPortalTabConfig>(new TNRTab());

    const [initState, setState] = useState<any>({});

    const isLoading = () => Object.keys(initState).map(key => initState[key] === true).reduce((prev, curr) => prev || curr, false);

    const updateInitState =
        (property: string, state: boolean) => setState(currState => {
            const newState = {...currState};
            newState[property] = state;
            return newState;
        });

    const stateInit: Initialization = {
        beginInitialize(): string {
            const randomProp = uuidv4();
            updateInitState(randomProp, true);
            return randomProp;
        }, finishInitialize(initializationId: string): void {
            updateInitState(initializationId, false);
        }
    };

    const onTabSelectionChange = (key: string) => {
        console.debug(`${selectedTab}->${key}`);
        setSelectedTab(key);
    };

    const reportSearchChange = (isExpert: boolean, reportName: string) => {
        const newState = {...allTabStates};
        newState[selectedTab] = {isExpertMode:isExpert, reportName}
        setAllTabStates(newState)
    };

    type ReportTab = {
        shouldRender: boolean;
        component: JSX.Element;
    }

    const tabs: ReportTab[] = [
        {
            shouldRender: entitlementExists(tnrTab.current.getEntitlement(), props.distinctEntitlements),
            component: <Tabs.TabPane
                tab={props.intl.formatMessage({
                    'id': 'tnr-reports',
                    'defaultMessage': "TNR Reports"
                })} key="item-1">
                <TNRReportPortalTab
                    intl={props.intl}
                    distinctEntitlements={props.distinctEntitlements}
                    currentUser={props.currentUser}
                    initialization={stateInit}
                    showReportOptions={false}
                    infoPortalTab={tnrTab.current}
                    onReportAndSearchTypeChange={reportSearchChange}/>
            </Tabs.TabPane>
        }
    ]

    useEffect(() => {
        const initialState:{[key: string]: AllTabsState} = {};
        tabs.forEach((item, idx) => {
            initialState[`item-${idx + 1}`] = {isExpertMode: false, reportName: ''};
        })
        setAllTabStates(initialState)
    }, []);

    const displayHeading = () => {
        if (allTabStates[selectedTab]) {
            if (!allTabStates[selectedTab]?.isExpertMode) {
                return props.intl.formatMessage({id: 'account-payable-basic-title'});
            }
            return props.intl.formatMessage({id: 'info-portal-expert-search-heading'}).replace('{}', props.intl.formatMessage({id:allTabStates[selectedTab].reportName}));
        }

        return props.intl.formatMessage({id: 'account-payable-basic-title'});

    };

    useEffect(()=> {
        function checkIfContentEditable(node: HTMLElement | null) {
            if (node === null) {
                return false;
            }

            if (node.getAttribute('contenteditable') === 'true') {
                return true;
            }

            return checkIfContentEditable(node.parentElement);
        }

        const TARGETS_BLACKLIST = ['textarea', 'input', 'select'];

        const enterKeyEvent =   (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            if (!target || e.key !== 'Enter') {
                return;
            }

            const eventPath = e.composedPath ? e.composedPath() : [];
            const fromChatWidget = eventPath.some((node) =>
                node instanceof HTMLElement && node.id === 'bmw-ckm-chat-widget'
            ) || !!target.closest?.('#bmw-ckm-chat-widget');

            if (fromChatWidget) {
                return;
            }

            const isBlacklistedTarget = TARGETS_BLACKLIST.indexOf(target.tagName.toLowerCase()) > -1;
            const isNonRadioInput = target.tagName.toLowerCase() === 'input' && !target.classList.contains('ant-radio-input');
            if ((isBlacklistedTarget && isNonRadioInput) || checkIfContentEditable(target)) {
                return;
            }

            const selectorString = `.ant-tabs-tabpane-active [data-testid="info-portal-search-button"]`;
            const searchButton = document.querySelector(selectorString);
            if (searchButton) {
                console.debug('Button found', searchButton);
                // @ts-ignore
                searchButton.click();
            }
        };

        console.log('Registering key up event');
        const target = document;
        target.addEventListener('keyup', enterKeyEvent);
        return () => {
            console.log('Unregistering key up event');
            target.removeEventListener('keyup', enterKeyEvent);
        };
    },[]);

    return <>
        <ConfigProvider locale={localStorage.getItem("locale") === "en" ? enUS : deDe}>
            <Spin size={"large"} spinning={isLoading()}>
                <div>
                    <ProPageHeader
                        prefixedClassName="site-page-header"
                        title={displayHeading()}
                    />
                    <Tabs onChange={onTabSelectionChange}>
                        {
                            tabs.filter(theTab => theTab.shouldRender).map(theTab => theTab.component)
                        }
                    </Tabs>
                </div>
            </Spin>
        </ConfigProvider>
    </>
};

export default AccountReceivableReports;